"use client";

import Link from "next/link";
import { format } from "date-fns";
import { de } from "date-fns/locale";

import { GridContainer } from "@/components/layout/GridContainer";
import { useNow } from "@/lib/hooks/useNow";
import { useMySignups } from "@/lib/hooks/useMySignups";
import { formatDateParam } from "@/lib/utils/recurrence";
import { cn } from "@/lib/utils";

type OccurrenceMeta = {
  displayDate: Date;
  originalDate: Date;
  dateChanged: boolean;
  isCancelled: boolean;
};

type EventDatePickerProps = {
  occurrences: OccurrenceMeta[];
  currentOriginalDate: Date;
  eventSlug: string;
  eventUuid: string;
};

const EventDatePicker = ({ occurrences, currentOriginalDate, eventSlug, eventUuid }: EventDatePickerProps) => {
  const { isSignedUp } = useMySignups();
  const now = useNow();
  const currentDay = formatDateParam(currentOriginalDate);

  // Filter: hide dates older than 1 week, dedup by original datetime
  const visible = occurrences
    .filter((o) => {
      if (!now) return true; // Show all on first render (matches server)
      const oneWeekAgo = new Date(now);
      oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
      return o.displayDate >= oneWeekAgo;
    })
    .filter((o, i, arr) => arr.findIndex((a) => a.originalDate.getTime() === o.originalDate.getTime()) === i);

  if (visible.length <= 1) return null;

  return (
    <div className="bg-sand-10 pt-6 pb-2">
      <GridContainer>
        <div className="col-span-12 md:col-span-8 md:col-start-3 lg:col-span-8 lg:col-start-3 flex flex-col gap-3">
          <p className="text-copy text-gray-75 text-center">Alle Termine</p>
          <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-hide justify-center">
            {visible.map((occ) => {
              const dateParam = formatDateParam(occ.originalDate);
              const isSelected = dateParam === currentDay;
              const signedUp = isSignedUp(eventUuid, occ.originalDate.toISOString());
              const isPast = now ? occ.displayDate < now : false;

              return (
                <Link
                  key={dateParam}
                  href={`${eventSlug}?date=${dateParam}`}
                  scroll={false}
                  className={cn(
                    "flex flex-col items-center justify-center rounded-full size-[72px] shrink-0 transition-all",
                    occ.isCancelled ? "opacity-40 line-through" :
                    signedUp ? "bg-sky-50" :
                    isSelected ? "bg-white" : "bg-sand-50 hover:bg-sand-75",
                    isPast && !occ.isCancelled && "!opacity-40"
                  )}
                >
                  <span className="text-headline text-black leading-none">{format(occ.displayDate, "dd", { locale: de })}</span>
                  <span className="text-copy-sm text-gray-75 leading-none mt-0.5">{format(occ.displayDate, "MMM", { locale: de })}</span>
                </Link>
              );
            })}
          </div>
        </div>
      </GridContainer>
    </div>
  );
};

export { EventDatePicker };
export type { OccurrenceMeta };
